import { KitIcon, UsersIcon } from 'components'
import { upperCase } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { useContext, useEffect, useRef } from 'react'
import { TemplateRoute } from 'types'
import { useElementSize } from 'utils'
import { useScale } from '../../utils/scaleUtils'
import { TraitWithSelections, useTraitsWithSelections } from '../../utils/traitUtils'
import GlassPanel from '../GlassPanel'
import { SessionContext, useExplorerConfig } from '../providers'
import { SDivider, SGrid, SIconCircle, SText, SView, SViewProps } from '../scaled'

interface Props extends SViewProps {
  goBackTo: (step: TemplateRoute) => void
}

export default function TraitProgressPanel({ goBackTo, ...rest }: Props) {
  const { getLanguageValue } = useContext(SessionContext)
  const responses = useTraitsWithSelections(true)
  const scale = useScale()
  const { templateDocs, facility } = useExplorerConfig()
  const { companyName, visitorNames } = facility || {}
  const { title, subtitle } = templateDocs?.template.welcome || {}

  const { ref: containerRef, height: containerHeight } = useElementSize()
  const { ref: responsesRef, height: responsesHeight } = useElementSize()
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scroll({ top: responsesHeight, behavior: 'smooth' })
  }, [responsesHeight])

  const hasVisitorNames = Boolean(visitorNames?.length)
  const showContinueText = !hasVisitorNames || responses.length

  return (
    <SView maxH="100%" {...rest}>
      <GlassPanel
        h={containerHeight / scale}
        position="absolute"
        transition={`height ${transition.duration}s`}
        inset={0}
      />

      <SView ref={containerRef} overflow="hidden" p={20} pb={0}>
        <SText fontSize={60} fontWeight="bold" fitToWidth="100%" fitToHeight={80} mb={20}>
          {companyName || getLanguageValue(title) || 'Welcome!'}
        </SText>

        <SView position="relative" mb={20}>
          <motion.div
            style={{ position: showContinueText ? 'static' : 'absolute', top: 0, left: 0 }}
            animate={showContinueText ? { opacity: 1, x: 0 } : { opacity: 0, x: '100%' }}
            initial={false}
            transition={transition}
          >
            <SText fontSize={22}>
              {getLanguageValue(subtitle) || 'Please continue to customize your experience.'}
            </SText>
          </motion.div>

          {hasVisitorNames && (
            <motion.div
              style={{ position: responses.length ? 'absolute' : 'static', top: 0, left: 0 }}
              animate={!responses.length ? { opacity: 1 } : { opacity: 0, x: '-100%' }}
              initial={false}
              transition={transition}
            >
              <SView direction="row" gap={10}>
                <SIconCircle Icon={UsersIcon} />
                <SView gap={5} pt={7}>
                  {visitorNames!.map((name, i) => (
                    <SText key={i} fontSize={20}>
                      {name}
                    </SText>
                  ))}
                </SView>
              </SView>
            </motion.div>
          )}
        </SView>

        <motion.div
          animate={!responses.length ? { opacity: 0, x: '100%' } : { opacity: 1, x: 0 }}
          transition={transition}
        >
          <SDivider />
        </motion.div>

        <SView ref={scrollRef} overflow="hidden auto">
          <SView ref={responsesRef} gap={20} py={Boolean(responses.length) ? 20 : 0}>
            <AnimatePresence>
              {responses.map(
                (response, i) =>
                  !response?.leadField && (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: '100%', y: '-40%' }}
                      exit={{ opacity: 0, x: '100%', y: '-40%' }}
                      animate={{ opacity: 1, x: 0, y: 0 }}
                      transition={transition}
                    >
                      <ProgressStep response={response} onPress={() => goBackTo(response.step)} />
                    </motion.div>
                  )
              )}
            </AnimatePresence>
          </SView>
        </SView>
      </SView>
    </SView>
  )
}

interface ProgressStepProps {
  onPress: () => void
  response: TraitWithSelections
}

function ProgressStep({ onPress, response }: ProgressStepProps) {
  const { getLanguageValue } = useContext(SessionContext)
  if (!response) return null

  const { selected, isMulti, values, title, icon } = response
  const titleString = upperCase(getLanguageValue(title))
  const items = values.map(v => ({
    ...v,
    isSelected: selected.some(s =>
      s.traitId ? s.traitId === v.id : s.title === getLanguageValue(v.title)
    ),
  }))

  if (isMulti) {
    return (
      <SView cursor="pointer" onClick={onPress} gap={10}>
        <SText fontSize={12} fontWeight="semibold">
          MY {titleString}S ARE:
        </SText>
        <SGrid columns={2} gap={5}>
          {items.map(({ title, isSelected }, i) => (
            <SText color={isSelected ? 'white' : '#C5C5C588'} key={i} fontSize={13}>
              • {getLanguageValue(title)}
            </SText>
          ))}
        </SGrid>
      </SView>
    )
  } else {
    const firstSelected = items.find(s => s.isSelected)
    if (!firstSelected) return null

    const valueString = getLanguageValue(firstSelected.title)
    const _icon = firstSelected.icon || icon || 'stack'

    return (
      <SView cursor="pointer" onClick={onPress} direction="row" gap={10}>
        <SIconCircle Icon={props => <KitIcon {...props} type={_icon} />} />
        <SView gap={2} style={{ flexShrink: 1 }}>
          <SText fontSize={13}>MY {titleString} IS</SText>
          <SText lineClamp={1} fontWeight="semibold" fontSize={20}>
            {valueString}
          </SText>
        </SView>
      </SView>
    )
  }
}

const transition = { ease: 'easeInOut' as const, duration: 0.5 }
